// =====================================================================
// src/components/BoloTab.tsx — v0.4.9.2.2
//
// Shared BOLO (Be On Look Out) form + history component.
// Used by:
//   - src/pages/missingPersonsAdmin.tsx  (targetType="missing_person")
//   - src/pages/mostWantedAdmin.tsx      (targetType="most_wanted")
//   - src/pages/stolenVehiclesAdmin.tsx  (targetType="stolen_vehicle")
//
// Supports two modes:
//   1. agentAdmin: orgId is the user's own org, no station picker
//   2. superadmin: orgId is omitted; component shows a station picker
//      + warning modal before issuing
// =====================================================================

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type BoloFromAPI, type BoloTargetType, type BoloScope } from '../services/api';
import { FiltersBar, applyFilters, type DateRange } from '../components/FiltersBar';

interface OrgOption {
  id: string;
  name: string;
  country: string;
}

interface Props {
  targetType: BoloTargetType;
  /** agentAdmin mode: the issuing org id (typically user.civil_respondent_id) */
  orgId?: string;
  /** superadmin mode: list of orgs to choose from */
  availableOrgs?: OrgOption[];
  /** Available targets for the picker */
  records: Array<{ id: string; label: string; country?: string; photoUrl?: string | null }>;
}

const SCOPES: BoloScope[] = ['network_wide', 'le_country', 'le_station'];

export function BoloTab({ targetType, orgId, availableOrgs, records }: Props) {
  const { t } = useTranslation();
  const isSuperAdminMode = Array.isArray(availableOrgs) && availableOrgs.length > 0;

  const [selectedOrgId, setSelectedOrgId] = useState<string>(orgId || '');
  const [bolos, setBolos] = useState<BoloFromAPI[]>([]);
  const [loading, setLoading] = useState(true);
  // v0.4.9.2.3c22ca: search + date-range filter on the BOLO history list.
  // Default = last 7 days; persists across BoloTab re-renders (same component
  // instance, only resets on full page reload).
  const [boloSearch, setBoloSearch] = useState('');
  const [boloDateRange, setBoloDateRange] = useState<DateRange>(() => {
    const end = new Date(); end.setHours(23, 59, 59, 999);
    const start = new Date(); start.setDate(start.getDate() - 6); start.setHours(0, 0, 0, 0);
    return { start, end };
  });
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [targetId, setTargetId] = useState('');
  const [scope, setScope] = useState<BoloScope>('network_wide');
  const [protocol, setProtocol] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Cancel / reissue in-flight
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [reissueId, setReissueId] = useState<string | null>(null);

  // SuperAdmin warning modal state
  const [warnModalOpen, setWarnModalOpen] = useState(false);
  const [warnAcknowledged, setWarnAcknowledged] = useState(false);

  const effectiveOrgId = isSuperAdminMode ? selectedOrgId : orgId || '';

  const refresh = useCallback(async () => {
    if (!effectiveOrgId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.listBolos(effectiveOrgId, { target_type: targetType });
      setBolos(result);
    } catch (err: any) {
      setError(err?.message || t('bolo.tab.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [effectiveOrgId, targetType]);

  useEffect(() => {
    if (effectiveOrgId) refresh();
  }, [effectiveOrgId, refresh]);

  // v0.4.9.2.3c22ca: derived filtered history. Searches across protocol text
  // (most user-visible field) + country, scope, ID prefix. Date filter is on
  // issued_at. Note this is purely client-side; bolos don't change here.
  const filteredBolos = applyFilters(
    bolos,
    boloSearch,
    boloDateRange,
    (b: any) => b.issued_at,
    (b: any) =>
      [
        b.protocol,
        b.country,
        b.scope,
        b.id,
        b.issuing_org_id,
      ]
        .filter(Boolean)
        .join(' ')
  );

  const doIssue = async () => {
    if (!effectiveOrgId) {
      setError(isSuperAdminMode ? t('bolo.tab.selectStation') : t('bolo.tab.noIssuingOrg'));
      return;
    }
    if (!targetId || !scope || !protocol.trim()) {
      setError(t('bolo.tab.allFieldsRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await api.issueBolo(effectiveOrgId, {
        target_type: targetType,
        target_id: targetId,
        scope,
        protocol: protocol.trim(),
      });
      setSuccessMsg(isSuperAdminMode ? t('bolo.tab.issuedTest') : t('bolo.tab.issued'));
      setTargetId('');
      setProtocol('');
      await refresh();
    } catch (err: any) {
      setError(err?.message || t('bolo.tab.issueFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleIssue = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSuperAdminMode) {
      // Show the warning modal first; the actual issue runs from the modal's confirm button
      setWarnAcknowledged(false);
      setWarnModalOpen(true);
    } else {
      doIssue();
    }
  };

  const handleCancel = async (boloId: string) => {
    if (!cancelReason.trim()) {
      setError(t('bolo.tab.cancelReasonRequired'));
      return;
    }
    setCancellingId(boloId);
    setError(null);
    try {
      await api.cancelBolo(boloId, cancelReason.trim());
      setCancelReason('');
      await refresh();
    } catch (err: any) {
      setError(err?.message || t('bolo.tab.cancelFailed'));
    } finally {
      setCancellingId(null);
    }
  };

  const handleReissue = async (boloId: string) => {
    setReissueId(boloId);
    setError(null);
    try {
      await api.reissueBolo(boloId);
      await refresh();
    } catch (err: any) {
      setError(err?.message || t('bolo.tab.reissueFailed'));
    } finally {
      setReissueId(null);
    }
  };

  const selectedOrg = isSuperAdminMode
    ? availableOrgs?.find((o) => o.id === selectedOrgId)
    : null;

  // v0.4.9.2.3c22cb: derive the selected target's country (for the
  // cross-org warning modal). The `records` prop already carries the
  // country per record (passed in from admin pages). Falls back to
  // undefined when the user hasn't picked a target yet.
  const selectedTarget = records.find((r) => r.id === targetId);
  const targetCountry = selectedTarget?.country;
  const isCrossOrg = Boolean(
    isSuperAdminMode &&
      selectedOrg &&
      targetCountry &&
      selectedOrg.country &&
      selectedOrg.country !== targetCountry
  );

  return (
    <div className="space-y-6">
      {/* Superadmin station picker */}
      {isSuperAdminMode && (
        <div className="border border-border rounded p-4 bg-yellow-50">
          <h3 className="text-sm font-semibold mb-2">
            {t('bolo.stationPickerTitle', 'Issuing station (superadmin)')}
          </h3>
          <select
            value={selectedOrgId}
            onChange={(e) => setSelectedOrgId(e.target.value)}
            className="w-full p-2 border border-input rounded bg-background text-sm"
          >
            <option value="">
              {t('bolo.selectStation', '— Select a station —')}
            </option>
            {availableOrgs!.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name} ({o.country})
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground mt-2">
            {t(
              'bolo.stationPickerHint',
              'Select the police precinct or civil response station that this BOLO will be issued on behalf of. The BOLO will appear to recipients as if it was issued by that station.'
            )}
          </p>
        </div>
      )}

      {/* Issue form (only shown when an org is selected / available) */}
      {effectiveOrgId && (
        <div className="border border-border rounded p-4 bg-card">
          <h3 className="text-sm font-semibold mb-3">
            {t('bolo.issueTitle', 'Issue a new BOLO')}
          </h3>
          <form onSubmit={handleIssue} className="space-y-3" autoComplete="off">
            <div>
              <label className="block text-xs font-medium mb-1">
                {t('bolo.targetLabel', 'Target')}
              </label>
              <select
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                required
                className="w-full p-2 border border-input rounded bg-background text-sm"
              >
                <option value="">
                  {t('bolo.targetPlaceholder', '— Select a record —')}
                </option>
                {records.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                    {r.country ? ` (${r.country})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">
                {t('bolo.scopeLabel', 'Scope')}
              </label>
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value as BoloScope)}
                required
                className="w-full p-2 border border-input rounded bg-background text-sm"
              >
                {SCOPES.map((s) => (
                  <option key={s} value={s}>
                    {t(`bolo.scope.${s}`, s)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">
                {t('bolo.protocolLabel', 'Protocol')}
              </label>
              <textarea
                value={protocol}
                onChange={(e) => setProtocol(e.target.value)}
                required
                rows={3}
                placeholder={t(
                  'bolo.protocolPlaceholder',
                  'Describe the action to take if this target is identified...'
                )}
                className="w-full p-2 border border-input rounded bg-background text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="bg-primary text-primary-foreground px-4 py-2 rounded text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {submitting
                ? t('bolo.issuing', 'Issuing...')
                : t('bolo.issueButton', 'Issue BOLO')}
            </button>
            {successMsg && (
              <p className="text-sm text-green-600 mt-2">{successMsg}</p>
            )}
            {error && <p className="text-sm text-destructive mt-2">{error}</p>}
          </form>
        </div>
      )}

      {/* History list */}
      <div>
        <h3 className="text-sm font-semibold mb-3">
          {t('bolo.historyTitle', 'BOLO history')}
        </h3>
        {!effectiveOrgId ? (
          <p className="text-sm text-muted-foreground">
            {t('bolo.noHistoryNoOrg', 'Select a station above to see its BOLO history.')}
          </p>
        ) : loading ? (
          <p className="text-sm text-muted-foreground">
            {t('common.loading', 'Loading...')}
          </p>
        ) : bolos.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('bolo.noHistory', 'No BOLOs issued yet.')}
          </p>
        ) : (
          <>
            {/* v0.4.9.2.3c22ca: search + date-range filter strip */}
            <FiltersBar
              searchValue={boloSearch}
              onSearchChange={setBoloSearch}
              searchPlaceholder="Search BOLOs by protocol, country, scope…"
              dateRange={boloDateRange}
              onDateRangeChange={setBoloDateRange}
            />
            {filteredBolos.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">
                {t('bolo.history.noMatchesLong')}
              </p>
            ) : (
              <ul className="space-y-2">
                {filteredBolos.map((b) => (
                <li
                  key={b.id}
                  className={`border border-border rounded p-3 bg-card ${
                    b.cancelled_at ? 'opacity-60' : ''
                  }`}
                >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1 flex-wrap">
                      <span className="font-mono">{b.id.slice(0, 8)}</span>
                      <span>•</span>
                      <span>{t(`bolo.scope.${b.scope}`, b.scope)}</span>
                      <span>•</span>
                      <span>{b.country}</span>
                      <span>•</span>
                      <span>
                        {t('bolo.issuedAt', 'Issued')}{' '}
                        {new Date(b.issued_at).toLocaleString()}
                      </span>
                      {b.issuance_count > 1 && (
                        <>
                          <span>•</span>
                          <span>
                            {t('bolo.issuanceCount', 'Re-issued {{count}}x', {
                              count: b.issuance_count,
                            })}
                          </span>
                        </>
                      )}
                      {b.cancelled_at && (
                        <span className="text-destructive font-semibold">
                          • {t('bolo.cancelled', 'CANCELLED')}
                        </span>
                      )}
                    </div>
                    {/* v0.4.9.2.3c22cb: surface target_name + target_photo_url
                        (returned by enrichBoloTargets) so a stolen-vehicle
                        BOLO card shows the license plate + thumbnail when
                        present. Falls back to protocol-only when null. */}
                    {b.target_name && (
                      <p className="text-sm font-semibold text-foreground mb-1">
                        {b.target_name}
                      </p>
                    )}
                    <div className="flex items-start gap-3">
                      {b.target_photo_url && (
                        <img
                          src={b.target_photo_url}
                  alt={b.target_name || t('bolo.tab.targetAlt')}
                          className="w-16 h-16 object-cover rounded border border-border flex-shrink-0"
                        />
                      )}
                      <p className="text-sm whitespace-pre-wrap break-words flex-1 min-w-0">
                        {b.protocol}
                      </p>
                    </div>
                    {b.target_description && (
                      <p className="text-xs text-muted-foreground mt-1 italic">
                        {b.target_description}
                      </p>
                    )}
                    {b.cancelled_reason && (
                      <p className="text-xs text-destructive mt-1 italic">
                        {t('bolo.cancelReason', 'Reason')}: {b.cancelled_reason}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    {!b.cancelled_at && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleReissue(b.id)}
                          disabled={reissueId === b.id}
                          className="text-xs px-2 py-1 border border-border rounded hover:bg-accent disabled:opacity-50"
                        >
                          {t('bolo.reissue', 'Re-issue')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setCancellingId(b.id)}
                          disabled={cancellingId === b.id}
                          className="text-xs px-2 py-1 border border-destructive text-destructive rounded hover:bg-destructive/10 disabled:opacity-50"
                        >
                          {t('bolo.cancel', 'Cancel')}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {cancellingId === b.id && (
                  <div className="mt-2 flex gap-2">
                    <input
                      type="text"
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder={t('bolo.cancelReasonPlaceholder', 'Reason for cancellation')}
                      className="flex-1 p-1 border border-input rounded text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => handleCancel(b.id)}
                      className="bg-destructive text-destructive-foreground px-3 py-1 rounded text-xs"
                    >
                      {t('bolo.confirmCancel', 'Confirm')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCancellingId(null);
                        setCancelReason('');
                      }}
                      className="px-3 py-1 border border-border rounded text-xs"
                    >
                      {t('common.cancel', 'Cancel')}
                    </button>
                  </div>
                )}
              </li>
            ))}
              </ul>
            )}
          </>
        )}
      </div>

      {/* SuperAdmin warning modal — v0.4.9.2.3c22cb: extended copy when
          the target's country differs from the issuing station's country
          (cross-org issuance). Non-cross-org case keeps the prior text. */}
      {warnModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-card border border-destructive rounded-xl shadow-xl w-full max-w-md p-5 my-8">
            <h3 className="font-bold text-destructive mb-2">
              ⚠ {t('bolo.warnTitle', 'Broadcast to live police mobiles')}
            </h3>
            {isCrossOrg ? (
              <>
                <p className="text-sm mb-3">
                  {t(
                    'bolo.warnCrossOrgBody',
                    'You (SuperAdmin) are issuing a BOLO for a target in a different country than the issuing station.'
                  )}
                </p>
                <ul className="text-sm mb-3 space-y-1 pl-4 list-disc">
                  <li>
                    <strong>{t('bolo.warnIssuingStation', 'Issuing station')}:</strong>{' '}
                    {selectedOrg?.name ?? '—'}
                    {selectedOrg?.country ? ` (${selectedOrg.country})` : ''}
                  </li>
                  <li>
                    <strong>{t('bolo.warnTargetCountry', 'Target country')}:</strong>{' '}
                    {targetCountry}
                  </li>
                </ul>
                <p className="text-sm mb-3">
                  {t(
                    'bolo.warnCrossOrgBody2',
                    'The BOLO will be tagged with the issuing station above, but recipients will see the target country as its own. Double-check this is what you intend — recipients in the issuing station\'s country may not be the right audience for a target elsewhere.'
                  )}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm mb-3">
                  {t(
                    'bolo.warnBody',
                    'You (SuperAdmin) are about to issue a BOLO that will be broadcast in real time to all connected devices of'
                  )}{' '}
                  <strong>{selectedOrg?.name ?? '—'}</strong>{' '}
                  {selectedOrg?.country ? `(${selectedOrg.country})` : ''}.
                </p>
                <p className="text-sm mb-3">
                  {t(
                    'bolo.warnBody2',
                    'This should only be done if explicitly authorized by the police station (e.g., as a service/favor due to a logistics or technical issue they may be experiencing).'
                  )}
                </p>
              </>
            )}
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={warnAcknowledged}
                onChange={(e) => setWarnAcknowledged(e.target.checked)}
                className="mt-1"
              />
              <span>
                {isCrossOrg
                  ? t(
                      'bolo.warnCrossOrgAck',
                      'I confirm this cross-country issuance is intentional and authorized.'
                    )
                  : t(
                      'bolo.warnAck',
                      'I confirm this issuance is authorized by the station named above.'
                    )}
              </span>
            </label>
            <div className="mt-4 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => {
                  setWarnModalOpen(false);
                  setWarnAcknowledged(false);
                }}
                className="px-4 py-2 border border-input rounded-lg hover:bg-accent text-sm"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setWarnModalOpen(false);
                  doIssue();
                }}
                disabled={!warnAcknowledged}
                className="px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:opacity-90 disabled:opacity-50 text-sm"
              >
                {t('bolo.warnConfirmIssue', 'Issue BOLO anyway')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
